import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo } from 'react';
import SVGIcon from '../../components/SVGIcon';
import { PostType } from '../../graphql/generated/graphql';
import publicIcon from '../../icons/non-prefilled/16x16/public.svg';
import PostDetailPreviewPopOver from '../../post/PostDetailPreviewPopOver';
import { getStateIcon } from '../../post/PostIcon';
import {
  isAllowedThroughLinkAccess,
  PostPolicyAction,
} from '../../post/postPolicyCheck';
import { UserRole } from '../../topic/topicPolicyCheck';
import postPath, { publicPostPath } from '../../utils/url/postPath';
import useIsPublicView from '../../utils/useIsPublicView';
import { parsePostMentionId } from '../utils/matchMention';
import InaccessibleMentionHoverCard from './InaccessibleMentionHoverCard';
import MentionBase from './MentionBase';
import styles from './PostMention.module.scss';
import { gql } from '../../graphql/generated/gql';

const POST_QUERY = gql(`
  query PostMentionPost($id: ID!) {
    post(id: $id) {
      id
      title
      type
      ...isAllowedThroughLinkAccess_post
    }
  }
`);

const PUBLIC_POST_QUERY = gql(`
  query PostMentionPublicPost($id: ID!) {
    post: publicPost(id: $id) {
      id
      title
      ...isAllowedThroughLinkAccess_post
    }
  }
`);

interface PostMentionProps {
  id: string;
  content: string | null;
  mentionText?: string | null;
  updateCache?: (name?: string) => void;
  link: boolean;
  showHoverCard?: boolean;
  hideIcon?: boolean;
  selected?: boolean;
  singleLine?: boolean;
  newTab?: boolean;
}

const PostMention = ({
  id: mentionId,
  content,
  mentionText,
  updateCache,
  link,
  showHoverCard,
  hideIcon,
  selected,
  singleLine,
  newTab,
}: PostMentionProps) => {
  const isPublicView = useIsPublicView();
  const { anchor, id: postId } = useMemo(
    () => parsePostMentionId(mentionId),
    [mentionId],
  );

  const { data, loading: isDataLoading } = useQuery(POST_QUERY, {
    variables: { id: postId },
    skip: isPublicView,
  });

  const { data: publicData, loading: isPublicDataLoading } = useQuery(
    PUBLIC_POST_QUERY,
    { variables: { id: postId }, skip: !isPublicView },
  );

  const post = isPublicView ? publicData?.post : data?.post;
  const isLoading = isPublicView ? isPublicDataLoading : isDataLoading;

  const isPubliclyAccessible = post
    ? isAllowedThroughLinkAccess(
        PostPolicyAction.ViewPost,
        post,
        UserRole.Anonymous,
      )
    : false;

  const path =
    isPublicView && isPubliclyAccessible
      ? publicPostPath({ id: postId, title: content ?? '' }, anchor)
      : postPath(post ?? { id: postId, title: content ?? '' }, anchor);

  useEffect(() => {
    if (!isPublicView && post && updateCache) {
      updateCache(post.title);
    }
  }, [updateCache, isPublicView, post]);

  const isTemplate =
    post && 'type' in post ? post.type === PostType.Template : false;

  const commonProps = {
    icon: hideIcon
      ? undefined
      : getStateIcon(
          {
            type: isTemplate ? PostType.Template : PostType.Post,
            draft: false,
            archived: false,
          },
          !isTemplate,
        ),
    type: isTemplate ? ('template' as const) : ('post' as const),
    to: link ? path : undefined,
    content: mentionText ?? post?.title ?? content ?? undefined,
    singleLine,
    newTab,
  };

  const mention = (
    <MentionBase
      {...commonProps}
      content={mentionText ?? post?.title ?? content ?? undefined}
      iconAfter={
        isPubliclyAccessible && (
          <SVGIcon inline className={styles.publicIcon} size={14}>
            {publicIcon}
          </SVGIcon>
        )
      }
    />
  );

  if (!showHoverCard || isLoading || selected) {
    return mention;
  }

  if (!post) {
    return (
      <MentionBase
        {...commonProps}
        popOver={<InaccessibleMentionHoverCard mention="post" />}
      />
    );
  }

  return (
    <PostDetailPreviewPopOver
      allowCrossAxisFlip
      display="inline"
      postId={post.id}
      anchor={anchor}
    >
      {mention}
    </PostDetailPreviewPopOver>
  );
};

export default PostMention;
